import axios from 'axios';
import { empezar, terminar } from './cargando';

/**
 * Opciones propias que se pueden pasar a cualquier llamada:
 *
 *   api.get('/songs', { silencioso: true })   ← sin velo de carga
 *
 * `silencioso` es para lo que se pide mientras la persona escribe (el
 * buscador): ahí un velo a pantalla completa sería insoportable.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    silencioso?: boolean;
  }
}


let baseURL: string;

// En 'production' (npm run build) siempre el API real.
if (process.env.NODE_ENV === 'production') {
  baseURL = 'https://api.miensamble.com';
} else {
  // En desarrollo el backend vive en la misma máquina que sirve esta página,
  // así que se deduce del host actual. Fijarlo a "localhost" rompía al abrir
  // la web desde el teléfono: ahí "localhost" es el propio teléfono.
  // El puerto es 4001 y no 4000 porque quiosco ocupa el 4000 en esta máquina.
  baseURL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:4001`;
}

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});


/**
 * Velo de carga: las escrituras lo muestran de inmediato porque la persona
 * acaba de pulsar Guardar y necesita saber que se está haciendo algo; las
 * lecturas solo si tardan, para no parpadear en cada pantalla.
 */
const esEscritura = (metodo?: string) =>
  ['post', 'put', 'patch', 'delete'].includes((metodo || 'get').toLowerCase());

// Adjunta el JWT a cada petición
api.interceptors.request.use(
  (config) => {
    if (!config.silencioso) empezar(esEscritura(config.method));
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => { terminar(); return Promise.reject(error); }
);

// Sesión vencida o sin permisos → volver al login
api.interceptors.response.use(
  (response) => {
    if (!response.config?.silencioso) terminar();
    return response;
  },
  (error) => {
    if (!error.config?.silencioso) terminar();
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.setItem('sessionExpired', '1');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/** Mensaje de error legible desde una respuesta del API. */
export const apiError = (err: any, fallback = 'Ocurrió un error'): string =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

export default api;
